import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRecipientTimeline, importContacts, searchContacts } from '@/lib/contacts-api';

export const contactKeys = {
  search: (query: string, skip: number) => ['contacts', query, skip] as const,
};

export function useContacts(query: string, skip: number) {
  return useQuery({
    queryKey: contactKeys.search(query, skip),
    queryFn: () => searchContacts({ query, skip, take: 50 }),
    // The list is being typed against, so a moment of stale data beats a flash of skeleton on every
    // keystroke's debounce.
    staleTime: 10_000,
  });
}

/**
 * The CSV import.
 *
 * Deliberately NOT optimistic. An import's whole value is its per-row outcome — how many were
 * created, updated, skipped and why — and there is nothing to predict optimistically. Guessing here
 * would mean showing counts that turn out to be wrong.
 */
export function useImportContacts() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: importContacts,
    onSuccess: async () => {
      // Every contact list is invalidated rather than the current filter, because an import can add
      // rows that match any search.
      await client.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export const timelineKeys = {
  detail: (contactId: string, before: string | undefined) =>
    ['contact-timeline', contactId, before ?? 'first'] as const,
};

/**
 * One person's delivery history.
 *
 * `staleTime: 0` — deliberately the opposite of the contact list beside it. This is read while
 * somebody is on the phone saying they did not receive an email, and a cached answer from four minutes
 * ago is exactly the answer that wastes the call.
 */
export function useRecipientTimeline(contactId: string | undefined, before?: string) {
  return useQuery({
    queryKey: timelineKeys.detail(contactId ?? 'none', before),
    // The cursor is spread in rather than passed as `before: undefined`, because
    // `exactOptionalPropertyTypes` treats an explicit undefined as a different thing from an absent
    // property — and the API function's contract is "absent means the first page".
    queryFn:
      contactId === undefined
        ? skipToken
        : () => getRecipientTimeline({ contactId, ...(before === undefined ? {} : { before }) }),
    staleTime: 0,
  });
}
